import { useEffect, useState } from "react";
import { Plus, Store } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";

const EMPTY_FORM = {
  name: "",
  address: "",
  phone: "",
  email: "",
};

export default function OutletsPage() {
  const { session, refresh } = useAuth();

  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileOutlet, setProfileOutlet] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [profileForm, setProfileForm] = useState(EMPTY_FORM);

  const [togglingId, setTogglingId] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const isOwner = session?.user?.role === "OWNER";

  const outletLimit = session?.features?.OUTLET_LIMIT?.limit ?? null;
  const planName = session?.subscription?.plan_name || session?.subscription?.plan_code || "paket saat ini";

  const load = async () => {
    try {
      const { data } = await api.get("/outlets");
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();

    try {
      await api.post("/outlets", form);
      toast.success("Outlet created");
      setShowCreate(false);
      setForm(EMPTY_FORM);
      await load();
      await refresh();
    } catch (err2) {
      toast.error(apiError(err2));
    }
  };

  const toggleStatus = async (outlet) => {
    if (!isOwner || togglingId) return;

    const requestedActive = !outlet.active;

    setTogglingId(outlet.id);

    try {
      await api.patch(`/outlets/${outlet.id}/status`, {
        active: requestedActive,
      });

      toast.success(
        requestedActive
          ? `${outlet.name} diaktifkan`
          : `${outlet.name} dinonaktifkan`
      );

      await load();
      await refresh();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;

      if (code === "OUTLET_LIMIT_REACHED") {
        const limitText =
          outletLimit === null
            ? ""
            : ` Paket ${planName} hanya memperbolehkan maksimal ${outletLimit} outlet aktif.`;

        toast.error(
          `Anda sudah mencapai batas maksimal jumlah outlet.${limitText}`
        );
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setTogglingId(null);
    }
  };

  const openProfile = (outlet) => {
    setProfileOutlet(outlet);
    setProfileForm({
      name: outlet.name || "",
      address: outlet.address || "",
      phone: outlet.phone || "",
      email: outlet.email || "",
    });
    setShowProfile(true);
  };

  const saveProfile = async (e) => {
    e.preventDefault();

    if (!profileOutlet || !isOwner) return;

    setSavingProfile(true);

    try {
      await api.patch(`/outlets/${profileOutlet.id}`, profileForm);

      toast.success("Outlet profile saved");
      setShowProfile(false);

      await load();
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const activeCount = items.filter((o) => o.active === true).length;
  const showLimitBanner =
    outletLimit !== null && items.length > outletLimit;

  return (
    <div data-testid="outlets-page">
      <PageHeader
        title="Outlets"
        subtitle="Manage your business locations"
        testid="outlets-header"
      >
        {isOwner && (
          <button
            onClick={() => setShowCreate(true)}
            data-testid="outlet-create-button"
            className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2 hover:opacity-90 active:scale-[0.98]"
          >
            <Plus size={16} /> New Outlet
          </button>
        )}
      </PageHeader>

      {showLimitBanner && (
        <div
          className="mb-4 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm"
          data-testid="outlet-limit-banner"
        >
          <p className="font-semibold">
            Paket {planName} memperbolehkan maksimal {outletLimit} outlet aktif.
          </p>
          <p className="mt-1 text-muted-foreground">
            Anda memiliki {items.length} outlet terdaftar dan {activeCount} outlet
            aktif. Gunakan toggle ACTIVE/INACTIVE untuk mengatur outlet yang
            digunakan, atau upgrade paket untuk menggunakan lebih banyak outlet
            aktif.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((o) => {
          const busy = togglingId === o.id;

          return (
            <div
              key={o.id}
              className="bg-card border border-border rounded-2xl p-5"
              data-testid={`outlet-card-${o.code}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                  <Store size={18} />
                </div>

                <span className="font-mono text-xs text-muted-foreground">
                  {o.code}
                </span>
              </div>

              <h3 className="font-heading font-semibold mt-3">
                {o.name}
              </h3>

              <p className="text-xs text-muted-foreground mt-1">
                {o.address || "—"}
              </p>

              <p className="text-xs text-muted-foreground">
                {o.phone || ""}
              </p>

              <div className="mt-4 flex items-center justify-between gap-3">
                <span
                  className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                    o.active
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-red-500/15 text-red-400"
                  }`}
                >
                  {o.active ? "ACTIVE" : "INACTIVE"}
                </span>

                {isOwner ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={o.active}
                    aria-label={`${o.name} ${o.active ? "ACTIVE" : "INACTIVE"}`}
                    disabled={busy}
                    onClick={() => toggleStatus(o)}
                    data-testid={`outlet-toggle-${o.code}`}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition ${
                      o.active
                        ? "bg-emerald-500/20 border-emerald-500/40"
                        : "bg-secondary border-border"
                    } ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full transition-transform ${
                        o.active
                          ? "translate-x-6 bg-emerald-400"
                          : "translate-x-1 bg-muted-foreground"
                      }`}
                    />
                  </button>
                ) : null}
              </div>

              <button
                type="button"
                onClick={() => openProfile(o)}
                data-testid={`outlet-profile-${o.code}`}
                className="mt-4 w-full h-10 rounded-xl bg-secondary border border-border text-sm font-semibold hover:bg-accent transition"
              >
                Outlet Profile
              </button>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowCreate(false)}
        >
          <form
            onSubmit={create}
            className="bg-card border border-border w-full max-w-md rounded-2xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="outlet-create-modal"
          >
            <h3 className="font-heading text-lg font-bold">
              New Outlet
            </h3>

            {[
              ["name", "Outlet name"],
              ["address", "Address"],
              ["phone", "Phone"],
              ["email", "Email"],
            ].map(([k, label]) => (
              <input
                key={k}
                data-testid={`outlet-form-${k}`}
                value={form[k]}
                onChange={(e) =>
                  setForm({ ...form, [k]: e.target.value })
                }
                placeholder={label}
                required={k === "name"}
                type={k === "email" ? "email" : "text"}
                className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              />
            ))}

            <button
              type="submit"
              data-testid="outlet-form-submit"
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
            >
              Create Outlet
            </button>
          </form>
        </div>
      )}

      {showProfile && profileOutlet && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !savingProfile && setShowProfile(false)}
        >
          <form
            onSubmit={saveProfile}
            className="bg-card border border-border w-full max-w-xl rounded-2xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="outlet-profile-modal"
          >
            <div>
              <h3 className="font-heading text-lg font-bold">
                Outlet Profile
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {profileOutlet.code} · {profileOutlet.name}
              </p>
            </div>

            {[
              ["name", "Outlet name"],
              ["address", "Address"],
              ["phone", "Phone"],
              ["email", "Email"],
            ].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">
                  {label}
                </label>

                <input
                  data-testid={`outlet-profile-${k}`}
                  value={profileForm[k]}
                  onChange={(e) =>
                    setProfileForm({
                      ...profileForm,
                      [k]: e.target.value,
                    })
                  }
                  disabled={!isOwner || savingProfile}
                  type={k === "email" ? "email" : "text"}
                  className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none disabled:opacity-60"
                />
              </div>
            ))}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowProfile(false)}
                disabled={savingProfile}
                className="flex-1 h-11 rounded-xl bg-secondary border border-border font-semibold text-sm disabled:opacity-50"
              >
                Close
              </button>

              {isOwner && (
                <button
                  type="submit"
                  disabled={savingProfile}
                  data-testid="outlet-profile-save"
                  className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-50"
                >
                  {savingProfile ? "Saving..." : "Save Profile"}
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
